import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import SessionDialog from '../../sessions/components/SessionDialog'
import { parseUtcDate } from '../../../utils/dates'

const Field = styled.div`
  margin-bottom: 14px;
`
const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
`
const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  &:focus {
    outline: 2px solid ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    outline-offset: 1px;
  }
`
const TextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  &:focus {
    outline: 2px solid ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    outline-offset: 1px;
  }
`

const SegmentRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const SegmentBtn = styled.button.attrs({ type: 'button' })`
  flex: 1 1 auto;
  min-width: 110px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid
    ${(p) =>
      p.$active ? p.theme?.color?.primary || '#1F3A5F' : p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) =>
    p.$active ? p.theme?.color?.primary || '#1F3A5F' : 'transparent'};
  color: ${(p) => (p.$active ? '#fff' : p.theme?.color?.ink || '#1F2937')};
  font-family: inherit;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
`

const TIME_BANDS = [
  { id: 'MORNING', label: 'Morning', hint: 'before 12pm' },
  { id: 'AFTERNOON', label: 'Afternoon', hint: '12 – 5pm' },
  { id: 'EVENING', label: 'Evening', hint: 'after 5pm' },
]

const MODES = [
  { id: 'OFFLINE', label: 'In-clinic' },
  { id: 'ONLINE', label: 'Video call' },
]

function todayIso() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}

function combineToIso(dateStr, band) {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map((s) => parseInt(s, 10))
  if (!y || !m || !d) return null
  const hour = band === 'MORNING' ? 10 : band === 'AFTERNOON' ? 15 : band === 'EVENING' ? 18 : 12
  const date = parseUtcDate(y, m - 1, d, hour, 0, 0, 0)
  return date.toISOString()
}

/**
 * Patient-portal "Request appointment" dialog.
 * Lightweight free-form ask: mode (in-clinic/video), preferred date,
 * preferred time band (morning/afternoon/evening), optional note.
 * Submits to POST /api/secure/me/sessions/request — the practitioner
 * confirms or proposes an alternative slot.
 */
export default function RequestAppointmentDialog({
  open,
  onClose,
  onSubmit,
  defaultPractitionerId,
  defaultPractitionerName,
  defaultMode = 'OFFLINE',
}) {
  const [mode, setMode] = useState(defaultMode)
  const [date, setDate] = useState('')
  const [band, setBand] = useState('MORNING')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setMode(defaultMode)
      setDate('')
      setBand('MORNING')
      setNote('')
      setError(null)
    }
  }, [open, defaultMode])

  const minDate = useMemo(() => todayIso(), [])
  const canSubmit = !!defaultPractitionerId && !!date

  async function handleSubmit() {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      const preferredIso = combineToIso(date, band)
      await onSubmit({
        practitionerId: defaultPractitionerId,
        preferredMode: mode,
        preferredDate: date,
        preferredTimeBand: band,
        preferredSlots: preferredIso ? [preferredIso] : [],
        reason: note.trim() || null,
      })
      onClose()
    } catch (e) {
      setError(e?.message || 'Could not send request. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SessionDialog
      open={open}
      title={
        defaultPractitionerName
          ? `Request appointment with ${defaultPractitionerName}`
          : 'Request appointment'
      }
      subtitle="Pick when and how you'd like to meet. Your practitioner will confirm a time."
      primaryLabel="Send request"
      primaryDisabled={!canSubmit}
      primaryLoading={busy}
      onPrimary={handleSubmit}
      onClose={onClose}
    >
      <Field>
        <Label>Mode</Label>
        <SegmentRow>
          {MODES.map((m) => (
            <SegmentBtn key={m.id} $active={mode === m.id} onClick={() => setMode(m.id)}>
              {m.label}
            </SegmentBtn>
          ))}
        </SegmentRow>
      </Field>

      <Field>
        <Label>Preferred date</Label>
        <Input
          type="date"
          min={minDate}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </Field>

      <Field>
        <Label>Time of day</Label>
        <SegmentRow>
          {TIME_BANDS.map((b) => (
            <SegmentBtn key={b.id} $active={band === b.id} onClick={() => setBand(b.id)}>
              <div>{b.label}</div>
              <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400 }}>{b.hint}</div>
            </SegmentBtn>
          ))}
        </SegmentRow>
      </Field>

      <Field>
        <Label>Anything to add (optional)</Label>
        <TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What you'd like to discuss, ongoing symptoms, etc."
        />
      </Field>

      {error && (
        <div
          style={{
            color: '#C24D49',
            fontSize: 13,
            marginTop: -4,
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}
    </SessionDialog>
  )
}
